import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import { useTranslation } from 'react-i18next';
import './forecast.css';
import { FORECAST_YEARS, FieldKind, fitsDecimals, numKeyDown } from './forecastUtils';

interface Props {
    show: boolean;
    label: string;              // field label, e.g. "Units added each month"
    kind: FieldKind;            // percent_years | currency_years | number_years
    noCarry?: boolean;          // blank year = "continue" instead of "same as previous year"
    values: any[];              // Year 1..5 as typed in the form ('' = blank)
    onClose: () => void;
    onSave: (values: any[]) => void;
}

/**
 * Pop-up to enter a field's value for each forecast year (Year 1-5).
 * A blank year carries the previous year's value (or, for restart-type fields,
 * simply continues from the previous month).
 */
const YearValuesModal: React.FC<Props> = ({ show, label, kind, noCarry, values, onClose, onSave }) => {
    const { t } = useTranslation('scenes.FiveYearForecast');
    const [draft, setDraft] = useState<any[]>([]);
    const [error, setError] = useState<string>('');

    useEffect(() => {
        if (show) {
            setDraft(Array.from({ length: FORECAST_YEARS }, (_, i) => values?.[i] ?? ''));
            setError('');
        }
    }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

    const apply = () => {
        const bad = draft.some((v: any) => v !== '' && v !== undefined && !Number.isNaN(Number(v)) && Number(v) < 0);
        if (bad) return setError(String(t('entry.noNegative', '{{field}} cannot be negative.', { field: label })));
        onSave(draft);
    };

    const placeholder = (i: number): string => {
        if (i === 0) return '';
        if (noCarry) return '';
        for (let j = i - 1; j >= 0; j--) if (draft[j] !== '' && draft[j] !== undefined) return String(draft[j]);
        return '';
    };

    return (
        <Modal show={show} onHide={onClose} centered className="fyf-modal" dialogClassName="fyf-years-modal">
            <Modal.Header closeButton>
                <Modal.Title className="h6 mb-0">{label} — {t('entry.byYearTitle', 'by year')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="fyf-note mb-3">
                    {noCarry
                        ? t('entry.byYearHintRestart', 'Year 1 is the starting value. Fill a later year only if it restarts at a new level in its first month; leave it blank to continue from the previous month.')
                        : t('entry.byYearHint', 'Leave a year blank to keep the previous year\'s value.')}
                </p>
                <div className="fyf-years-list">
                    {draft.map((v, i) => (
                        <div className="fyf-years-row" key={i}>
                            <Form.Label className="mb-0 fw-bold" htmlFor={`fyf-yv-${i}`}>
                                {t('common.year', 'Year {{n}}', { n: i + 1 })}
                            </Form.Label>
                            <div className="input-group">
                                {kind === 'currency_years' && <span className="input-group-text">$</span>}
                                <Form.Control id={`fyf-yv-${i}`} className="fyf-edit" type="number" step="any" min={0}
                                    onKeyDown={numKeyDown}
                                    value={v} placeholder={placeholder(i)}
                                    onChange={(e) => { if (fitsDecimals(e.target.value)) { setDraft((d) => d.map((x, k) => (k === i ? e.target.value : x))); setError(''); } }} />
                                {kind === 'percent_years' && <span className="input-group-text">%</span>}
                            </div>
                        </div>
                    ))}
                </div>
                {error && <div className="fyf-error mt-3">{error}</div>}
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="fyf-secondary" onClick={() => setDraft((d) => d.map((x, i) => (i === 0 ? x : '')))}>
                    {t('entry.byYearClear', 'Clear Years 2–5')}
                </button>
                <button type="button" className="fyf-secondary" onClick={onClose}>{t('common.cancel', 'Cancel')}</button>
                <button type="button" className="fyf-primary" onClick={apply}>{t('common.apply', 'Apply')}</button>
            </Modal.Footer>
        </Modal>
    );
};

export default YearValuesModal;
